var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { SafariWriteDirectionUtility } from '@inter-ikea-kompis/utilities';
import { FocusOutlinePositionWatcherEnum } from '@inter-ikea-kompis/component-focus-outline';
import { FocusOutline } from '@inter-ikea-kompis/component-focus-outline';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';
import { LoadingBall as SkapaLoadingBall } from '@ingka/loading-webc/passive.js';
import ButtonAlignEnum from '../../enums/ButtonAlignEnum.js';
import ButtonSizeEnum from '../../enums/ButtonSizeEnum.js';
import ButtonTypeEnum from '../../enums/ButtonTypeEnum.js';
import ButtonStyle from './ButtonStyle.js';
/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 * This component should be used for general buttons and it provides with general stylings according to NCL and fixes for common browsers and touch devices.
 *
 * @event click Triggered when button is clicked.
 */
export default class Button extends KompisElement {
    constructor() {
        super(...arguments);
        // Text on button.
        this.label = null;
        // Explanatory accessibility text for button. Should only be used when label isn't clear enough.
        this.ariaLabel = null;
        // Appearance and function 'primary' or 'secondary'.
        this.type = ButtonTypeEnum.primary;
        // Align the icon 'right' or 'left'.
        this.alignIcon = ButtonAlignEnum.left;
        // InvertedColors mode
        this.invertedColors = false;
        // Flip the icon horizontally in RTL context
        this.flipIconRtl = false;
        // Icon: Icon on button.
        this.icon = null;
        // If set to true button is disabled.
        this.disabled = false;
        // Button size.
        this.size = ButtonSizeEnum.medium;
        // Expanding and shrinking.
        this.expanding = false;
        // Renders the loading style
        this.loading = false;
        // Tab index.
        this.tabIndex = 0;
        // Aria-describedby text.
        this.ariaDescribedby = null;
        // Sets the behaviour of the position watcher. The position watcher is useful when the component is inside an overflown element where it can become cropped. When enabled it will be set itself to be position fixed and update the position of itself live. However, updating the position live can be slow and result in lag on slower computers. Be wary of using 'auto' or 'enabled' if the component have any ancestors with transform styling.
        this.focusOutlinePositionWatcher = FocusOutlinePositionWatcherEnum.auto;
    }
    render() {
        const direction = SafariWriteDirectionUtility.getWriteDirection(this);
        return html `
			<style>
				${ButtonStyle.getStyle({
            alignIcon: this.alignIcon,
            isDisabled: this.disabled,
            isLoading: this.loading,
            icon: this.icon,
            buttonType: this.type,
            invertedColors: this.invertedColors,
            direction: direction,
            size: this.size,
            theme: this.theme,
            expanding: this.expanding
        })}
			</style>
			<kompis-focus-outline
				.theme="${this.theme}"
				.positionWatcher="${this.focusOutlinePositionWatcher}"
				.invertedColors="${this.invertedColors}"
			>
				<button
					@mousedown="${(event) => event.preventDefault()}"
					autocomplete="off"
					aria-busy="${this.loading}"
					aria-label="${this.ariaLabel ?? this.label}"
					aria-describedby="${this.ariaDescribedby ?? ''}"
					tabindex="${this.tabIndex}"
					?disabled="${this.disabled || this.loading}"
				>
					<div class="container">${this.getIcon()} ${this.getDivider()} ${this.getLabel()}</div>
					${this.getLoadingIcon()}
				</button>
			</kompis-focus-outline>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-loading-ball', SkapaLoadingBall);
        this.customElements.define('kompis-icon', Icon);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-focus-outline', FocusOutline);
        super.connectedCallback();
    }
    /**
     * Returns icon.
     *
     * @returns Template.
     */
    getIcon() {
        if (!this.icon) {
            return null;
        }
        return html `
			<kompis-icon
				.theme="${this.theme}"
				.icon="${this.icon}"
				.flipIconRtl="${this.flipIconRtl}"
			></kompis-icon>
		`;
    }
    getDivider() {
        if (!this.icon || !this.label) {
            return null;
        }
        return html `<div class="divider"></div>`;
    }
    getLabel() {
        if (!this.label) {
            return null;
        }
        return html `
			<kompis-text
				.theme="${this.theme}"
				.type="${this.size === ButtonSizeEnum.small
            ? ThemeFontStyleTypeEnum.bodySmall
            : ThemeFontStyleTypeEnum.bodyMedium}"
				.bold="${true}"
			>
				${this.label}
			</kompis-text>
		`;
    }
    getLoadingIcon() {
        if (!this.loading || this.disabled) {
            return null;
        }
        return html `
			<div class="loading-indicator-container">
				<skapa-loading-ball
					.colour="${this.getLoadingIndicatorColor()}"
					?small="${this.size === ButtonSizeEnum.small}"
					?medium="${this.size === ButtonSizeEnum.medium}"
				>
				</skapa-loading-ball>
			</div>
		`;
    }
    getLoadingIndicatorColor() {
        switch (this.type) {
            case ButtonTypeEnum.primary:
            case ButtonTypeEnum.emphasised:
                return this.invertedColors ? 'primary' : 'secondary';
            case ButtonTypeEnum.secondary:
            case ButtonTypeEnum.tertiary:
                return this.invertedColors ? 'secondary' : 'primary';
            default:
                return 'secondary';
        }
    }
}
__decorate([
    property({ type: String })
], Button.prototype, "label", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "ariaLabel", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "type", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "alignIcon", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "invertedColors", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "flipIconRtl", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "disabled", void 0);
__decorate([
    property({ type: Object, required: true })
], Button.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "size", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "expanding", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "loading", void 0);
__decorate([
    property({ type: Number })
], Button.prototype, "tabIndex", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "ariaDescribedby", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "focusOutlinePositionWatcher", void 0);
//# sourceMappingURL=Button.js.map